import {SupportedNetwork} from 'src/types';
import {
  btc,
  eth,
  polygon,
  base,
  sol,
  tempo,
  sui,
} from '@sail/icons/react/BrandIcon';
import {absurd} from 'src/utils/absurd';
import type {OpenapiPostV1CryptoInternalRelayQuoteInputInputChainEnum} from '@stripe-internal/data-v1/schema/types';

type Network =
  | SupportedNetwork
  | OpenapiPostV1CryptoInternalRelayQuoteInputInputChainEnum;

const EVM_CHAIN_ID_MAPPING: {
  [chainId: number]: SupportedNetwork;
} = {
  1: 'ethereum',
  11155111: 'ethereum',
  137: 'polygon',
  80002: 'polygon',
  8453: 'base',
  84532: 'base',
  4217: 'tempo',
  42431: 'tempo',
};

export const getNetworkFromChainId = (
  chainId: number,
): SupportedNetwork | undefined => {
  return EVM_CHAIN_ID_MAPPING[chainId];
};

export const getChainIdFromNetwork = (
  network: SupportedNetwork,
  livemode = false,
): number => {
  // Return the appropriate chain ID based on network and environment
  switch (network) {
    case 'ethereum':
      return livemode ? 1 : 11155111;
    case 'polygon':
      return livemode ? 137 : 80002;
    case 'base':
      return livemode ? 8453 : 84532;
    case 'tempo':
      return livemode ? 4217 : 42431;
    default:
      throw new Error(`Unsupported network: ${network}`);
  }
};

export const getNetworkNameForLivemode = (
  network: SupportedNetwork,
  livemode: boolean,
): string => {
  switch (network) {
    case 'ethereum':
      return livemode ? 'Ethereum' : 'ETH Sepolia';
    case 'polygon':
      return livemode ? 'Polygon' : 'POL Amoy';
    case 'solana':
      return livemode ? 'Solana' : 'SOL Devnet';
    case 'base':
      return livemode ? 'Base' : 'BASE Sepolia';
    case 'tempo':
      return livemode ? 'Tempo' : 'Tempo Testnet';
    case 'sui':
      return livemode ? 'Sui' : 'SUI Testnet';
    default:
      throw new Error('Unsupported network');
  }
};

export const getNetworkName = (network: Network): string => {
  switch (network) {
    case 'bitcoin':
      return 'Bitcoin';
    case 'ethereum':
      return 'Ethereum';
    case 'polygon':
      return 'Polygon';
    case 'solana':
      return 'Solana';
    case 'base':
      return 'Base';
    case 'tempo':
      return 'Tempo';
    case 'sui':
      return 'Sui';
    default:
      throw new Error('Unsupported network');
  }
};

export const getNetworkIcon = (network: Network) => {
  switch (network) {
    case 'bitcoin':
      return btc;
    case 'ethereum':
      return eth;
    case 'polygon':
      return polygon;
    case 'base':
      return base;
    case 'solana':
      return sol;
    case 'tempo':
      return tempo;
    case 'sui':
      return sui;
    default:
      return absurd(network);
  }
};

export const getReadableNetworkType = (network: SupportedNetwork): string => {
  if (network === 'solana') return 'Solana';
  if (network === 'sui') return 'Sui';
  return 'EVM';
};
